import { CONSTANT } from './../../constant/constant';
import { environment } from './../../environments/environment';
import { MsgService } from './../share/msg/msg.service';
import { CryptJsSettingsInAutoAct } from './../../constant/crypt-js-settings';
import { CardSigninService } from './../signin/card-signin/card-signin.service';
import { AuthenticateService } from './../share/authenticate/authenticate.service';
import { IAutoAct } from './auto-act-if';
import { BehaviorSubject, Observable } from 'rxjs';
import 'rxjs/add/observable/interval';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { AutoActService } from './auto-act.service';
import { AutoActConstant } from './auto-act-constant';
import { ICardSignin } from '../signin/card-signin/card-signin-if';
import { CryptJsManager } from '../share/crypt-js/crypt-js-manager';
import { AutoActStateService } from './auto-act-state.service';
import { ErrorInterceptor } from '../interceptors/error.interceptor';
import { UtilService } from '../share/util/util.service';
import { BrowserLogging } from './browser-logging';
import { ItadakimasuOpe } from './itadakimasu-ope';
declare var $;

/** いただきます端末用の非同期処理実行用Component
 * 自動サインイン・サインアウト、定期ログ出力、いただきます端末設定取得などを実施する。
 */
@Component({
  selector: 'app-auto-act',
  templateUrl: './auto-act.component.html',
  styleUrls: ['./auto-act.component.scss']
})
export class AutoActComponent implements OnInit, OnDestroy {
  /** 定期的に自動処理用の値をDBから取得する */
  private getAutoActInfoSub: Subscription;
  private getAutoActInfoInterval = 1000 * 1800;
  /** 定期的にlocalStorageの食堂のオープン設定とjs現在時刻を比較する */
  private checkExecutingAutoActSub: Subscription;
  private checkExecutingAutoActInterval = 1000 * 10;
  /** 定期的にブラウザが保持するデータをDBに出力する */
  private postBrowserLogsSub: Subscription;
  private postBrowserLogsInterval = 1000 * 900;
  /** 定期的に（D)いただきます端末設定を参照し、未処理アクションを実行する */
  private itadakimasuOpeSub: Subscription;
  private itadakimasuOpeInterval = 1000 * 900;
  /** ログ表示用変数 */
  public displayLogs = '';
  public hasSmile9IdInLocalstorage = new BehaviorSubject<boolean>(false);
  public enableAutoSignIn = new BehaviorSubject<boolean>(false);
  public enableAutoSignOut = new BehaviorSubject<boolean>(false);
  /** 手動サインインした後、すぐに自動サインアウト処理が走った場合、いただきます画面の初期処理がトークン無しで実行される時があるので、
    その際にエラーメッセージが出ないよう、自動サインアウト中フラグOFFまで余裕を持たすためのsetTimeout()  */
  private falseToIsAutoSignOutTimeout: NodeJS.Timer = null;
  private readonly browserLogging: BrowserLogging;
  private readonly itadakimasuOpe: ItadakimasuOpe;

  constructor(
    private reqApi: AutoActService,
    private authServ: AuthenticateService,
    private cardSignInServ: CardSigninService,
    private autoActState: AutoActStateService,
    private msgServ: MsgService,
    private utilServ: UtilService,
  ) {
    this.browserLogging = new BrowserLogging(reqApi);
    this.itadakimasuOpe = new ItadakimasuOpe(reqApi);
  }

  async ngOnInit() {
    // いただきます端末設定の値をDBから定期的に取得する
    this.getAutoActInfoSub = Observable.interval(this.getAutoActInfoInterval)
      .subscribe(() => {
        this.getAutoActInfoAsync();
      });

    // localStorageのいただきます端末設定を参照し、自動処理を実施する。判定は定期的にする。
    this.checkExecutingAutoActSub = Observable.interval(this.checkExecutingAutoActInterval)
      .subscribe(() => {
        this.checkExecutingAutoActAsync();
      });

    // ブラウザが保持するデータをDB（t_l_itadakimasu_status）に定期的にログ出力する
    // NOTE: データ量が膨大になり、DBへの負荷が大きいため、現在は機能停止している。API側はそのままにしているので今後対応したい。
    // this.postBrowserLogsSub = Observable.interval(this.postBrowserLogsInterval)
    // .subscribe(() => {
    //   this.browserLogging.postLogAsync();
    // });

    // （D)いただきます端末操作の未処理アクションを参照し、あれば実行する
    this.itadakimasuOpeSub = Observable.interval(this.itadakimasuOpeInterval)
    .subscribe(() => {
      this.itadakimasuOpe.checkReadyActionAsync();
    });

    // サインイン, サインアウト時にいただきます端末設定の値を取得する。カードサインイン成功時などにいただきます端末設定を反映させたいので追記している。
    this.authServ.onSignInSucceeded
      .subscribe(_ => {
        this.getAutoActInfoAsync();
      });
    this.authServ.onSignOutSucceeded
      .subscribe(_ => {
        this.getAutoActInfoAsync();
      });

    // Observable.intervalは○ミリ秒後の遅延実行なのでComponentの初期処理にも実行させるため、下記に定期実行時に呼ばれるメソッドを追記している。
    await this.getAutoActInfoAsync();
    // NOTE: データ量が膨大になり、DBへの負荷が大きいため、現在は機能停止している。API側はそのままにしているので今後対応したい。
    // await this.browserLogging.postLogAsync();
  }

  ngOnDestroy() {
    this.killAllAutoAct();
    this.autoActState.resetAllParams();
  }

  /** いただきます端末設定の値をDBから取得する */
  private async getAutoActInfoAsync(): Promise<IAutoAct.AutoActInfo> {
    this.displayLog('try getAutoActInfo...');
    /* localStorageに暗号化されたSmile9IDがなければ、Angular上「いただきます端末」とはみなさずに自動処理用の値はDB取得させない。
    * localStorageの暗号化されたSmile9IDは、カードサインイン時にSmile9ロール＝いただきますの場合にのみ格納される。、 */
    let smile9IdWithPrefix = localStorage.getItem('[AutoAct]Smile9ID');
    if (!smile9IdWithPrefix || smile9IdWithPrefix === '') {
      this.hasSmile9IdInLocalstorage.next(false);
      return;
    } else {
      this.hasSmile9IdInLocalstorage.next(true);
    }
    // localStorageのSmile9IDを復号して利用
    smile9IdWithPrefix = CryptJsManager.decrypt(smile9IdWithPrefix, CryptJsSettingsInAutoAct.KEY, CryptJsSettingsInAutoAct.IV);

    // DBから最新のいただきます端末設定を取得する
    const model: IAutoAct.GetAutoActInfo = {
      smile9Id: smile9IdWithPrefix,
      ug_id: localStorage.getItem('[AutoAct]ugId'),
    };
    const result: IAutoAct.AutoActInfo | void = await this.reqApi.getAutoActInfo(model).catch(err => console.error(err));
    if (result) {
      if (result && result.itadakimasuSettings.length >= 1) {
        // 文字拡大モードのON/OFF判定
        const bigFontMode: IAutoAct.ItadakimasuSettings = result.itadakimasuSettings.find(x => x.item === CONSTANT.ITADAKIMASU_SETTINGS.BIG_FONT_MODE);
        if (bigFontMode && bigFontMode.item && AutoActStateService.bigFontMode !== (/true/i).test(bigFontMode.value)) {
          AutoActStateService.bigFontMode = (/true/i).test(bigFontMode.value);
        }
        // ヘッダ部の非表示設定のON/OFF判定
        const hideHeader: IAutoAct.ItadakimasuSettings = result.itadakimasuSettings.find(x => x.item === CONSTANT.ITADAKIMASU_SETTINGS.HIDE_HEADER);
        if (hideHeader && hideHeader.item && AutoActStateService.hideHeader !== (/true/i).test(hideHeader.value)) {
          AutoActStateService.hideHeader = (/true/i).test(hideHeader.value);
        }
        // スキャン結果表示時間の設定
        const displayMillisecOnScanResult: IAutoAct.ItadakimasuSettings = result.itadakimasuSettings.find(x => x.item === CONSTANT.ITADAKIMASU_SETTINGS.DISPLAY_MILLISEC_ON_SCAN_RESULT);
        if (displayMillisecOnScanResult && displayMillisecOnScanResult.item && AutoActStateService.displayMillisecOnScanResult !== (Number)(displayMillisecOnScanResult.value)) {
          AutoActStateService.displayMillisecOnScanResult = (Number)(displayMillisecOnScanResult.value);
          console.log(`this.autoActState.displayMillisecOnScanResult: ${AutoActStateService.displayMillisecOnScanResult}`);
        }

        // 暗号化していただきます端末設定の各種値をlocalStorageに格納。以降は非同期処理で定期的にこのlocalStorageを参照して自動サインインなどを判定する。
        localStorage.setItem('[AutoAct]autoActInfo', CryptJsManager.encrypt(JSON.stringify(result), CryptJsSettingsInAutoAct.KEY, CryptJsSettingsInAutoAct.IV));

        this.enableAutoSignIn.next(this.hasAutoSignIn(result));
        this.enableAutoSignOut.next(this.hasAutoSignOut(result));
      } else {
        localStorage.removeItem('[AutoAct]autoActInfo');
      }
      return result;
    } else {
      return null;
    }
  }

  /** localStorageの食堂のオープン設定とjs現在時刻を比較し、自動サインイン・サインアウトさせる。 */
  private async checkExecutingAutoActAsync(): Promise<boolean> {
    this.displayLog('try checkExecutingAutoAct...');
    let autoActInfoStr: string = localStorage.getItem('[AutoAct]autoActInfo');
    if (!autoActInfoStr) {
      return false;
    }
    // localStorageの自動処理用の値を復号して利用
    autoActInfoStr = CryptJsManager.decrypt(autoActInfoStr, CryptJsSettingsInAutoAct.KEY, CryptJsSettingsInAutoAct.IV);
    const autoActInfo: IAutoAct.AutoActInfo = JSON.parse(autoActInfoStr);
    let smile9IdWithPrefix = localStorage.getItem('[AutoAct]Smile9ID');
    if (!smile9IdWithPrefix || smile9IdWithPrefix === '') {
      return false;
    }
    // localStorageのSmile9IDを復号して利用
    smile9IdWithPrefix = CryptJsManager.decrypt(smile9IdWithPrefix, CryptJsSettingsInAutoAct.KEY, CryptJsSettingsInAutoAct.IV);

    const dateNow = new Date();
    const currentPath = location.pathname;
    // DBの食堂オープン、クローズ時刻をDate型に変換。原則オープン・クローズ時刻の日付は本日扱い、深夜設定の場合はクローズ時刻のみ翌日とする。
    const openSettings: IAutoAct.CafeOpenSettingsWithDateObj[] = [];
    autoActInfo.cafeOpenSettings
      .forEach(setting => {
        // jsで時刻比較する為、Date型に変換
        const settingDate: Date = this.utilServ.uTime.toDate(setting.date, '/');

        const openingDate: Date = new Date(
          settingDate.getFullYear(),
          settingDate.getMonth(),
          settingDate.getDate(),
          Number(setting.opening_time.split(':')[0]),
          Number(setting.opening_time.split(':')[1]),
          Number(setting.opening_time.split(':')[2])
        );
        const closingDate: Date = new Date(
          settingDate.getFullYear(),
          settingDate.getMonth(),
          settingDate.getDate(),
          Number(setting.closing_time.split(':')[0]),
          Number(setting.closing_time.split(':')[1]),
          Number(setting.closing_time.split(':')[2])
        );

        // 深夜の設定だった場合、現在日付のオープン・クローズ時刻と翌日のオープン・クローズ時刻を２つ生成(00:00:00〜クローズ時刻、オープン時刻〜23:59:59)
        if (closingDate < openingDate) {
          const midnightSetting1: IAutoAct.CafeOpenSettingsWithDateObj = JSON.parse(JSON.stringify(setting));
          const midnightSetting2: IAutoAct.CafeOpenSettingsWithDateObj = JSON.parse(JSON.stringify(setting));
          let settingDateTomorrow = new Date(settingDate);
          settingDateTomorrow.setDate(settingDateTomorrow.getDate() + 1);
          // 00:00:00〜クローズ時刻
          midnightSetting1.openingDate = new Date(
            settingDateTomorrow.getFullYear(),
            settingDateTomorrow.getMonth(),
            settingDateTomorrow.getDate(),
            0,
            0,
            0,
          );
          midnightSetting1.closingDate = new Date(
            settingDateTomorrow.getFullYear(),
            settingDateTomorrow.getMonth(),
            settingDateTomorrow.getDate(),
            Number(midnightSetting1.closing_time.split(':')[0]),
            Number(midnightSetting1.closing_time.split(':')[1]),
            Number(midnightSetting1.closing_time.split(':')[2])
          );
          midnightSetting1.date = this.utilServ.uTime.toDateString0Padding(midnightSetting1.openingDate);
          openSettings.push(midnightSetting1);

          // オープン時刻〜23:59:59
          midnightSetting2.openingDate = new Date(
            settingDate.getFullYear(),
            settingDate.getMonth(),
            settingDate.getDate(),
            Number(midnightSetting2.opening_time.split(':')[0]),
            Number(midnightSetting2.opening_time.split(':')[1]),
            Number(midnightSetting2.opening_time.split(':')[2])
          );
          midnightSetting2.closingDate = new Date(
            settingDate.getFullYear(),
            settingDate.getMonth(),
            settingDate.getDate(),
            23,
            59,
            59,
          );
          openSettings.push(midnightSetting2);
        } else {
          const normalSetting: IAutoAct.CafeOpenSettingsWithDateObj = JSON.parse(JSON.stringify(setting));
          normalSetting.openingDate = new Date(
            settingDate.getFullYear(),
            settingDate.getMonth(),
            settingDate.getDate(),
            Number(normalSetting.opening_time.split(':')[0]),
            Number(normalSetting.opening_time.split(':')[1]),
            Number(normalSetting.opening_time.split(':')[2])
          );
          normalSetting.closingDate = new Date(
            settingDate.getFullYear(),
            settingDate.getMonth(),
            settingDate.getDate(),
            Number(normalSetting.closing_time.split(':')[0]),
            Number(normalSetting.closing_time.split(':')[1]),
            Number(normalSetting.closing_time.split(':')[2])
          );
          openSettings.push(normalSetting);
        }
      });
    // js現在時刻が食堂営業時間内であるかどうか判定
    const currentOpenings: IAutoAct.CafeOpenSettingsWithDateObj[] = openSettings.filter(setting => {
      if (this.isSameDate(this.utilServ.uTime.toDate(setting.date, '/'), dateNow) &&
        setting.openingDate <= dateNow && dateNow <= setting.closingDate) {
        return true;
      } else {
        return false;
      }
    });

    // [カードサインイン画面表示中の場合]１つ以上の食堂が営業中かつ自動サインインが有効なら、自動サインイン実行
    if (currentPath === '/card-signin' && currentOpenings.length >= 1 && this.hasAutoSignIn(autoActInfo)) {
      await this.signInAsync(smile9IdWithPrefix);
      return true;
    }
    // [個人画面表示中の場合]１つ以上の食堂が営業中かつ自動サインインが有効なら、自動サインイン実行
    if (/^\/individual\/.*$/.test(currentPath) && currentOpenings.length >= 1 && this.hasAutoSignIn(autoActInfo)) {
      await this.signInAsync(smile9IdWithPrefix);
      return true;
    }
    // [カードスキャン系画面表示中の場合]全ての食堂が営業外かつ自動サインアウトが有効なら、自動サインアウト実行
    if (/^\/scan-card\/.*$/.test(currentPath) && currentOpenings.length === 0 && this.hasAutoSignOut(autoActInfo)) {
      this.signOut();
      return true;
    }

    return false;
  }

  /** 同じ日付であるか */
  private isSameDate(date1: Date, date2: Date): boolean {
    if (date1.getFullYear() === date2.getFullYear() &&
        date1.getMonth() === date2.getMonth() &&
        date1.getDate() === date2.getDate()) {
      return true;
    } else {
      return false;
    }
  }

  private hasAutoSignIn(autoActInfo: IAutoAct.AutoActInfo): boolean {
    if (autoActInfo.itadakimasuSettings.some(x => x.item === AutoActConstant.ItadakimasuSettings.auto_card_signin && x.value === AutoActConstant.AutoCardSignInValues.true)) {
      return true;
    }
    return false;
  }

  private hasAutoSignOut(autoActInfo: IAutoAct.AutoActInfo): boolean {
    if (autoActInfo.itadakimasuSettings.some(x => x.item === AutoActConstant.ItadakimasuSettings.auto_card_signout && x.value === AutoActConstant.AutoCardSignOutValues.true)) {
      return true;
    }
    return false;
  }

  /** 自動サインイン */
  private async signInAsync(smile9IdWithPrefix: string): Promise<boolean> {
    // 連続サインイン処理防止
    if (this.authServ.isLoading) {
      return false;
    }
    ErrorInterceptor.isAutoSignIn = true;
    const signInOk = await this.cardSignInAsync(smile9IdWithPrefix);
    // モーダルが開いた状態でページ遷移されると不要なDOM要素が残るので除去する
    $('body').removeClass('modal-open');
    $('.modal-backdrop').remove();
    if (signInOk) {
      // ページ遷移するとトースターが全消去されてしまうので、少しウェイトを挟んでからトースター表示する
      setTimeout(() => {
        this.msgServ.warning('食堂のオープン時間になった為、自動サインインしました', '');
      }, 3000);
    }
    ErrorInterceptor.isAutoSignIn = false;
    return signInOk;
  }

  /** カードサインイン */
  private cardSignInAsync(smile9IdWithPrefix: string): Promise<boolean> {
    this.displayLog('auto signin');
    return new Promise(resolve => {
      const query: ICardSignin.GetCognitoInfo = {
        felica_id: smile9IdWithPrefix,
      };
      this.authServ.isLoading = true;
      this.cardSignInServ.getCognitoInfo(query).subscribe(
        response => {
          this.authServ.isLoading = false;
          const cognitoInfo: ICardSignin.CognitoInfo = response;
          if (cognitoInfo.bear_name && cognitoInfo.shirokuma_code) {
            this.authServ.signIn(cognitoInfo.bear_name, cognitoInfo.shirokuma_code, smile9IdWithPrefix)
              .then(ok => resolve(true))
              .catch(err => resolve(false));
          } else {
            resolve(false);
          }
          resolve(true);
        },
        error => {
          this.authServ.isLoading = false;
          resolve(false);
        }
      );
    });
  }

  /** 自動サインアウト */
  private async signOut(): Promise<boolean> {
    this.displayLog('auto signout');
    // 連続サインアウト処理防止
    if (!this.authServ.token) {
      return;
    }
    // 意図しないタイミングで自動サインアウトフラグがOFFにならないよう、非同期処理をKILL
    if (this.falseToIsAutoSignOutTimeout !== null) {
      clearTimeout(this.falseToIsAutoSignOutTimeout);
    }
    ErrorInterceptor.isAutoSignOut = true;
    // モーダルが開いた状態でページ遷移されると不要なDOM要素が残るので除去する
    $('body').removeClass('modal-open');
    $('.modal-backdrop').remove();
    const signOutOk = await this.authServ.signOut();
    if (signOutOk) {
      // ページ遷移するとトースターが全消去されてしまうので、少しウェイトを挟んでからトースター表示する
      setTimeout(() => {
        this.msgServ.warning('食堂が準備中になった為、自動サインアウトしました', '');
      }, 3000);
    }
    // 手動サインインした後、すぐに自動サインアウト処理が走った場合、いただきます画面の初期処理がトークン無しで実行される時があるので、
    // その際にエラーメッセージが出ないよう、自動サインアウト中フラグOFFまで余裕を持たす
    this.falseToIsAutoSignOutTimeout = setTimeout(() => {
      ErrorInterceptor.isAutoSignOut = false;
    }, 5000);

    return signOutOk;
  }

  /** 全非同期処理無効化 */
  private killAllAutoAct() {
    this.displayLog('killAllAutoAct');
    if (!this.getAutoActInfoSub.closed) { this.getAutoActInfoSub.unsubscribe(); }
    if (!this.checkExecutingAutoActSub.closed) { this.checkExecutingAutoActSub.unsubscribe(); }
    if (!this.itadakimasuOpeSub.closed) { this.itadakimasuOpeSub.unsubscribe(); }
    // NOTE: データ量が膨大になり、DBへの負荷が大きいため、現在は機能停止している。API側はそのままにしているので今後対応したい。
    // if (!this.postBrowserLogsSub.closed) { this.postBrowserLogsSub.unsubscribe(); }
  }

  /** テスト用ログ出力 */
  public displayLog(str: string): void {
    // 本番環境では実行しない
    if (environment.name !== 'dev') {
      return;
    }
    // const currentDate = new Date();
    // const currentDateTimeStr = currentDate.toLocaleString('ja');
    // this.displayLogs = `[${currentDateTimeStr}]${str}\n` + this.displayLogs;
    console.log(str);
  }
}
